import { Spacing } from "../Spacing/Spacing";
import { useParentPosition } from "@/hooks/useParentPosition";
import { PInfo } from "../PInfo/PInfo";
import { useState, useRef, useEffect } from "react";
import { IconInfoCircle } from "@tabler/icons-react";

type SliderInputProps = {
  label?: string;
  checked: boolean;
  setChecked: () => void;
  infoMessage?: string;
  icon?: React.ElementType;
  infoIconSize?: number;
  iconSize?: number;
  color?: string;
  iconMargin?: string;
  infoIconPosition?: "left" | "right";
  font?: string;
  disabled?: boolean;
  hideInfoIcon?: boolean;
  testId?: string;
  /** Accessible name for the switch when no visible `label` is rendered (or
   *  when the visible label would collide with a nearby input's label). */
  ariaLabel?: string;
  /** Id of an element that describes the switch — e.g. why it is locked. */
  ariaDescribedBy?: string;
  /** Extra content after the label (the mobile pill). Opt-in: absent = the DOM as before. */
  labelAddon?: React.ReactNode;
};

export const SliderInput: React.FC<SliderInputProps> = ({
  label,
  checked,
  icon: Icon,
  setChecked,
  color = "font-secondary",
  iconMargin,
  iconSize,
  infoIconSize = 18,
  infoIconPosition = "left",
  infoMessage,
  font = "body-2-medium font-secondary",
  disabled = false,
  hideInfoIcon = false,
  testId,
  ariaLabel,
  ariaDescribedBy,
  labelAddon,
}) => {
  const pRef = useRef<HTMLParagraphElement>(null);
  const { dropdownPosition, setDropdownPosition } = useParentPosition();
  const [showTooltip, setShowTooltip] = useState(false);

  // 1. Live Tracking Effect (Scroll & Resize)
  useEffect(() => {
    if (showTooltip && pRef.current) {
      const updatePosition = () => {
        if (pRef.current) setDropdownPosition(pRef);
      };

      // Capture phase is required for nested scrolling
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showTooltip, setDropdownPosition]);

  // 2. Immediate Failsafe Effect
  useEffect(() => {
    if (showTooltip && pRef.current) {
      // Force calculation immediately when state becomes true
      setDropdownPosition(pRef);
    }
  }, [showTooltip, setDropdownPosition]);

  // 3. Centralized "Open" Logic
  const handleMouseEnter = () => {
    if (pRef.current) {
      // 1. Calculate position FIRST
      setDropdownPosition(pRef);
      // 2. Then show component
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div
      className="flex flex-row items-center w-full"
      onClick={(e) => e.stopPropagation()}
    >
      {Icon && <Icon size={iconSize} className={`${color} ${iconMargin}`} />}
      {label && (
        <>
          <div className="flex font-secondary items-center space-x-0.5 min-w-0">
            {infoMessage && infoIconPosition === "left" && !hideInfoIcon && (
              <IconInfoCircle
                size={infoIconSize}
                className={`${color} cursor-help mr-1`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            )}
            <p
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              ref={pRef}
              className={`${font} truncate ${infoMessage ? "cursor-help" : ""} `}
            >
              {label}
            </p>
            {labelAddon}
            {infoMessage && infoIconPosition === "right" && !hideInfoIcon && (
              <IconInfoCircle
                size={infoIconSize}
                className={`${color} cursor-help`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            )}
          </div>
          <Spacing width="flex-1" />
        </>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel ?? label}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        data-testid={testId}
        onMouseEnter={infoMessage ? handleMouseEnter : undefined}
        onMouseLeave={infoMessage ? handleMouseLeave : undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setChecked();
        }}
        className={`${
          disabled ? "opacity-40 cursor-default" : "cursor-pointer"
        } shrink-0 w-10 h-5 flex items-center rounded-full transition-colors ${
          checked
            ? "cta-primary"
            : "bg-(--color-outline-tertiary)"
        }`}
      >
        <div
          className={`w-4 h-4 rounded-full surface-primary ring-1 ring-black/5 transition-transform ${
            checked ? "translate-x-5.5" : "translate-x-0.5"
          }`}
        />
      </button>
      {showTooltip && infoMessage && (
        <PInfo message={infoMessage} position={dropdownPosition} />
      )}
    </div>
  );
};
